import { ApiRequestError } from './apiResponseError';

export const CHECKOUT_ERROR_CODES = {
  brandSpecialBudgetExhausted: 'brand_special_budget_exhausted',
} as const;

export type CheckoutErrorCode =
  (typeof CHECKOUT_ERROR_CODES)[keyof typeof CHECKOUT_ERROR_CODES];

export interface CheckoutErrorDetails {
  code: CheckoutErrorCode;
  title?: string | null;
}

export interface TaggedCheckoutError extends Error {
  checkoutErrorDetails?: CheckoutErrorDetails;
}

interface CheckoutErrorBody {
  errors?: unknown;
}

const isCheckoutErrorCode = (code: unknown): code is CheckoutErrorCode =>
  typeof code === 'string' &&
  (Object.values(CHECKOUT_ERROR_CODES) as string[]).includes(code);

/**
 * The two envelopes a refusal arrives in.
 *
 * v1 and kiosk send `errors` as an object — Checkout::RenderErrors merges the code in
 * alongside the message. v3 renders RFC 9457 Problem Details, and ProblemDetails.extract
 * wraps whatever it is handed into `[{detail: <the original hash>, code: "error"}]`, so
 * there the real code sits one level down under `detail`.
 */
const envelopes = (errors: unknown): unknown[] =>
  Array.isArray(errors)
    ? errors.map((entry) => (entry as { detail?: unknown } | null)?.detail)
    : [errors];

/**
 * Reads the details off an unwrapped error envelope. Unknown codes are ignored, so a
 * client that has not shipped copy for a new one falls back to its generic message.
 */
export const parseCheckoutErrorDetails = (
  errors: unknown
): CheckoutErrorDetails | undefined => {
  for (const envelope of envelopes(errors)) {
    const { code, title } = (envelope ?? {}) as {
      code?: unknown;
      title?: unknown;
    };

    if (isCheckoutErrorCode(code)) {
      return { code, title: typeof title === 'string' ? title : null };
    }
  }

  return undefined;
};

/**
 * Reads the details off either shape a failed checkout throws: an ApiRequestError
 * carrying the parsed body, or an Error the monolith request helper tagged.
 */
export const checkoutErrorDetails = (
  error: unknown
): CheckoutErrorDetails | undefined => {
  if (error instanceof ApiRequestError) {
    return parseCheckoutErrorDetails((error.body as CheckoutErrorBody)?.errors);
  }

  // @iheartjane/data-access's ApiError keeps the parsed envelope on `errors`.
  if (error instanceof Error && 'errors' in error) {
    const details = parseCheckoutErrorDetails(
      (error as CheckoutErrorBody).errors
    );
    if (details) return details;
  }

  return error instanceof Error
    ? (error as TaggedCheckoutError).checkoutErrorDetails
    : undefined;
};
